import React from 'react'
import { FIGURES, parseChordDisplayString, getDegreeDisplayLabel } from '../utils/chordFormatter'
import './ChordLabel.css'

/**
 * Affiche la partie chiffrage seule (baroque : superposé si 6/5, 6/4, 4/3, 5/4).
 * Utilisable avec une classe supplémentaire (ex. review-chord-wrong / review-chord-right).
 */
export function ChordLabelFigure({ figure, className = '' }) {
  if (!figure || figure === '5') return null
  const figObj = FIGURES.find(f => f.value === figure)
  const isStacked = figObj?.isStacked && figObj?.displayArray

  return (
    <span className={`chord-label-figure ${className}`.trim()}>
      {isStacked ? (
        <span className="chord-label-figure-stacked">
          {figObj.displayArray.map((d, i) => (
            <span key={i}>{d}</span>
          ))}
        </span>
      ) : (
        <span>{figObj?.display ?? figure}</span>
      )}
    </span>
  )
}

/**
 * Construit la partie "leading" (degré + altération + qualité) à partir d'un objet chord.
 * Respecte chord.degreeMode (generic/major/minor) pour la casse majeur/mineur comme le visualiseur élève.
 * @param {Object} chord
 * @param {{ borrowedGroupRole?: 'start'|'end'|'middle'|null }} opts - pour groupes emprunt : 'start' = seule "(", 'end' = seule ")", 'middle' = aucune, null = les deux si isBorrowed
 */
function buildLeadingFromChord(chord, opts = {}) {
  const { degree, accidental, quality, figure, isBorrowed, specialRoot, sixFourVariant, degreeMode, pedalDegree, borrowedFromDegree } = chord
  const role = opts.borrowedGroupRole
  const showOpening = isBorrowed && !borrowedFromDegree && (role === null || role === undefined || role === 'start')
  const showClosing = isBorrowed && !borrowedFromDegree && (role === null || role === undefined || role === 'end')

  if (specialRoot) {
    let leading = specialRoot === 'N' ? 'II' : { It: 'It+6', Fr: 'Fr+6', Gr: 'Gr+6' }[specialRoot] || specialRoot
    const figure = specialRoot === 'N' ? { stacked: false, value: '♭6' } : null
    if (showOpening) leading = '(' + leading
    return { leading, figure, pedalDegree: pedalDegree || null, borrowedFromDegree: borrowedFromDegree || null, trailingParen: showClosing || undefined }
  }

  const isI64 = degree === 'I' && figure === '64'
  const degreePartRaw = (degree === 'cad' || degree === 'Cad.') ? 'Cad.'
    : isI64 && sixFourVariant === 'passing' ? 'V'
    : isI64 && sixFourVariant === 'cadential' ? 'Cad.'
    : degree
  const degreePart = degreePartRaw === 'Cad.' ? 'Cad.' : (degreePartRaw && degreeMode)
    ? getDegreeDisplayLabel(degreePartRaw, degreeMode)
    : degreePartRaw

  let leading = ''
  if (showOpening) leading += '('
  if (accidental && degreePart !== 'Cad.') {
    leading += accidental === 'b' ? '♭' : accidental === '#' ? '♯' : '♮'
  }
  leading += degreePart || ''
  if (quality && degreePart !== 'Cad.') leading += quality
  const useTrailingParen = showClosing && figure && figure !== '5'
  if (showClosing && !useTrailingParen) leading += ')'

  if (!figure || figure === '5') return { leading, figure: null, pedalDegree: pedalDegree || null, borrowedFromDegree: borrowedFromDegree || null, trailingParen: useTrailingParen || undefined }
  const figObj = FIGURES.find(f => f.value === figure)
  const fig = figObj?.isStacked && figObj?.displayArray
    ? { stacked: true, digits: figObj.displayArray }
    : { stacked: false, value: figObj?.display ?? figure }
  return { leading, figure: fig, pedalDegree: pedalDegree || null, borrowedFromDegree: borrowedFromDegree || null, trailingParen: useTrailingParen || undefined }
}

/**
 * ChordLabel : affichage degré + chiffrage baroque (chiffres superposés si 6/5, 6/4, 4/3, 5/4).
 * Accepte soit un objet chord soit une chaîne displayString (ex. pour le QCM).
 * @param {'start'|'end'|'middle'|null} props.borrowedGroupRole - dans un groupe emprunt : seule "(" sur start, seule ")" sur end, aucune sur middle
 * @param {boolean} props.hidePedalInLabel - si vrai (ex. segment en groupe pédale multi-accord), ne pas afficher la fraction pédale dans le label
 */
export default function ChordLabel({ chord, displayString, className = '', ariaLabel, borrowedGroupRole = null, hidePedalInLabel = false }) {
  let leading = ''
  let figure = null
  let pedalDegree = null
  let borrowedFromDegree = null
  let trailingParen = false

  if (displayString != null && displayString !== '') {
    const parsed = parseChordDisplayString(displayString)
    leading = parsed.leading
    figure = parsed.figure
    borrowedFromDegree = parsed.borrowedFromDegree ?? null
    pedalDegree = hidePedalInLabel ? null : (parsed.pedalDegree ?? null)
  } else if (chord) {
    const built = buildLeadingFromChord(chord, { borrowedGroupRole })
    leading = built.leading
    figure = built.figure
    pedalDegree = hidePedalInLabel ? null : (built.pedalDegree ?? null)
    borrowedFromDegree = built.borrowedFromDegree ?? null
    trailingParen = !!built.trailingParen
  }

  if (!leading && !figure && !pedalDegree && !borrowedFromDegree) return null

  const title = ariaLabel ?? (typeof displayString === 'string' ? displayString : undefined)
  const chordPart = (
    <>
      {leading && <span className="chord-label-degree">{leading}</span>}
      {figure && (
        <span className="chord-label-figure">
          {figure.stacked ? (
            <span className="chord-label-figure-stacked">
              {figure.digits.map((d, i) => (
                <span key={i}>{d}</span>
              ))}
            </span>
          ) : (
            <span>{figure.value}</span>
          )}
        </span>
      )}
      {borrowedFromDegree && <><span className="chord-label-slash"> / </span><span className="chord-label-borrowed-degree">{borrowedFromDegree}</span></>}
      {trailingParen && <span className="chord-label-degree">)</span>}
    </>
  )

  return (
    <span
      className={`baroque-chord-label ${className}`.trim()}
      title={title}
      aria-label={title}
      role={title ? 'text' : undefined}
    >
      {pedalDegree ? (
        <span className="chord-label-pedal">
          <span className="chord-label-pedal-top">{chordPart}</span>
          <span className="chord-label-pedal-bar" aria-hidden="true" />
          <span className="chord-label-pedal-bottom">{pedalDegree}</span>
        </span>
      ) : (
        chordPart
      )}
    </span>
  )
}
